import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router';
import {
    AchatsAppDispatch,
    TiersAppDispatch,
    useAchatsService,
    useSociete,
    useTiersService,
} from '@europrocurement/l2d-domain';

import { navigateToRecapStatement } from '../../../../../../utils/navigationHelper';
import { UseHandleStatementProps } from '../types';
import HeaderButton from '../../../fragments/HeaderButton';
import StatementHeaderButton from '../../../fragments/StatementHeaderButton';

const useHandleStatement = ({
    mode,
    facture,
    mediaObject,
    achatsFromStatementDataSource,
}: UseHandleStatementProps) => {
    const [temporaryStatementId, setTemporaryStatementId] = useState<number>();
    const [supplierFromStatement, setSupplierFromStatement] = useState<number>();

    const { selectFournisseurAc } = useTiersService();
    const { getFactureAchatFromStatement, releveAchatApi } = useAchatsService();

    const tiersDispatch = useDispatch<TiersAppDispatch>();
    const achatsdispatch = useDispatch<AchatsAppDispatch>();
    const { enqueueSnackbar } = useSnackbar();

    const xIdSociete = useSociete();

    const { statementid } = useParams();
    const navigate = useNavigate();

    const searchForExistingInvoiceInStatement = useCallback<(statementId: number) => void>(
        (statementId: number) => {
            if (!achatsFromStatementDataSource) {
                console.error('Missing DataSource to handle statement !');
                return;
            }

            achatsdispatch({
                type: `${achatsFromStatementDataSource.slicename}/set${achatsFromStatementDataSource.name}Filter`,
                payload: { key: 'cloture', value: true },
            });

            achatsdispatch({
                type: `${achatsFromStatementDataSource.slicename}/set${achatsFromStatementDataSource.name}Filter`,
                payload: { key: 'deleted', value: false },
            });

            achatsdispatch({
                type: `${achatsFromStatementDataSource.slicename}/set${achatsFromStatementDataSource.name}Filter`,
                payload: { key: 'idReleveAchat', value: statementId },
            });

            setTimeout(() => {
                achatsdispatch(getFactureAchatFromStatement({}));
            }, 1000);
        },
        [achatsFromStatementDataSource, achatsdispatch, getFactureAchatFromStatement],
    );

    const createTemporaryStatement = useCallback(() => {
        if (mediaObject) {
            releveAchatApi
                .createReleveAchatReleveAchatCollection({
                    xIdSociete,
                    releveAchatJsonldReleveAchatCreateReleveAchatWrite: {
                        pdfFacture: mediaObject['@id'],
                    },
                })
                .then((response) => {
                    if (response && response.data && response.data.id) {
                        setTemporaryStatementId(response.data.id);
                        searchForExistingInvoiceInStatement(response.data.id);
                    }
                })
                .catch((reason) => {
                    console.error(reason);
                });
        } else {
            enqueueSnackbar('PDF manquant !', { variant: 'error' });
        }
    }, [
        enqueueSnackbar,
        mediaObject,
        releveAchatApi,
        searchForExistingInvoiceInStatement,
        xIdSociete,
    ]);

    const handleClickButtonToStatement = useCallback(() => {
        if (facture && facture.id_statement) {
            navigateToRecapStatement(navigate, facture.id_statement);
        }
    }, [facture, navigate]);

    const addStatementButton = useMemo<ReactNode>(() => {
        if (mode.type === 'update') return null;

        return (
            <StatementHeaderButton
                statementId={temporaryStatementId}
                onClick={() => {
                    createTemporaryStatement();
                }}
            />
        );
    }, [createTemporaryStatement, mode.type, temporaryStatementId]);

    const accessStatementButton = useMemo<ReactNode>(() => {
        if (!facture || !facture.id_statement) {
            // console.error(
            //     'Missing invoice or associate id statement, access to statement resume impossible !',
            // );
            return null;
        }

        return (
            <HeaderButton
                content="Récapitulatif de Relevé"
                onClick={handleClickButtonToStatement}
            />
        );
    }, [facture, handleClickButtonToStatement]);

    useEffect(() => {
        if (statementid && `${temporaryStatementId}` !== statementid) {
            releveAchatApi
                .getReleveAchatReleveAchatItem({
                    id: statementid,
                    xIdSociete,
                })
                .then((response) => {
                    if (response && response.data && response.data.id) {
                        setTemporaryStatementId(response.data.id);
                        searchForExistingInvoiceInStatement(response.data.id);
                    }
                });
        }
    }, [
        releveAchatApi,
        searchForExistingInvoiceInStatement,
        statementid,
        temporaryStatementId,
        xIdSociete,
    ]);

    /**
     * Select the publisher from the first invoice associated to the current statement.
     *
     * RG-Achat-60
     *
     * Invoices from the same statement should keeps the same publisher.
     */
    useEffect(() => {
        if (!achatsFromStatementDataSource) {
            // console.error("Missing DataSource to select statement's provider !");
            return;
        }

        if (
            achatsFromStatementDataSource.data.length > 0 &&
            achatsFromStatementDataSource.data[0].idEntiteFacturante
        ) {
            setSupplierFromStatement(achatsFromStatementDataSource.data[0].idEntiteFacturante);

            tiersDispatch(
                selectFournisseurAc({
                    id: achatsFromStatementDataSource.data[0].idEntiteFacturante,
                }),
            );
        }
    }, [achatsFromStatementDataSource, tiersDispatch, selectFournisseurAc]);

    const fetchInvoicesFromStatement = useCallback(() => {
        if (!temporaryStatementId) {
            // console.error('Missing statement id to fetch invoices !');
            return;
        }

        setTimeout(() => {
            searchForExistingInvoiceInStatement(temporaryStatementId);
        }, 1000);
    }, [searchForExistingInvoiceInStatement, temporaryStatementId]);

    return {
        addStatementButton,
        accessStatementButton,
        fetchInvoicesFromStatement,
        stateHandleStatement: {
            temporaryStatementId: {
                value: temporaryStatementId,
                set: setTemporaryStatementId,
            },
            supplierFromStatement: {
                value: supplierFromStatement,
                set: setSupplierFromStatement,
            },
        },
    };
};

export default useHandleStatement;
